import React, { useEffect, useRef, useState } from "react";
import {
  getAllLibrariesData,
  updateLibraryVisibility,
} from "../services/Libraries";
import { useVisibilityToggle } from "../hooks/useVisibilityToggle";
import { useRecordSync, isGridChangeRelevant } from "../hooks/useRecordSync";
import { useBulkSelection } from "../hooks/useBulkSelection";
import { useAutoOpenBulkEditPanel } from "../hooks/useAutoOpenBulkEditPanel";
import {
  readPersistedFilterState,
  usePersistFilterState,
} from "../hooks/usePersistedFilterState";
import { DataToolbar } from "../CommonComponents/Toolbar/DataToolbar";
import { ListView } from "../CommonComponents/ListView/ListView";
import { GridView } from "../CommonComponents/GridView/GridView";
import { Box, HStack, Stack, useToast } from "@chakra-ui/react";
import { FilterBox } from "../CommonComponents/Filter/FilterBox";
import {
  totalFilterCount,
  updateFacetsData,
  handleExport as handleExportUtil,
} from "../util/helper";
import { explore } from "../constants/constants";
import { useNavigate } from "react-router-dom";
import FilterPills from "../CommonComponents/Filter/FilterPills";
import FileNameFilter from "../CommonComponents/Filter/FileNameFilter";
import { withFileNameFilter } from "../util/fileNameFilter";
import SavedFiltersPanel from "../CommonComponents/Filter/SavedFilters/SavedFiltersPanel";
import BulkEditPanel from "../CommonComponents/BulkEdit/BulkEditPanel";
import SelectionToolbar from "../CommonComponents/BulkEdit/SelectionToolbar";

// #264008 (Tushar, 3 Nov): the export's columns don't match the originally uploaded sheet yet, so the
// Export button is hidden on this list until that is fixed. Set to true to bring it back.
const SHOW_EXPORT = false;

const LIBRARIES_SAVED_FILTERS_CONTEXT = "libraries";

const LibrariesTable = () => {
  // Restores whatever filter (ad-hoc or saved) was applied before the admin
  // navigated away to edit a record — otherwise this component remounting
  // fresh on the way back (browser back / navigate(-1)) would silently reset
  // to the blank default, discarding filters the admin had just applied.
  const persistedFilterState = readPersistedFilterState(
    LIBRARIES_SAVED_FILTERS_CONTEXT,
  );
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    persistedFilterState?.currentPage || 1,
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting state
  const [searchText, setSearchText] = useState(
    persistedFilterState?.searchText || "",
  );
  const [facets, setFacets] = useState([]);
  const [apply, setApply] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [filteredResultCount, setFilteredResultCount] = useState(0);
  const [sort, setSort] = useState([]);
  const [filterOptions, setFilterOptions] = useState(
    persistedFilterState?.filterOptions || {
      search: "",
      filters: {},
      gate: "AND",
      sortBy_v: "sortable_year",
    },
  );
  const [activeSidePanel, setActiveSidePanel] = useState(null); // "filter" | "bulkEdit" | null
  const selection = useBulkSelection();
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("viewPreference") || "list",
  );
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  const limit = 50; // Number of records per page

  // Exports every record matching the currently applied search/filters (not
  // just the visible page). Mirrors exactly what fetchLibraries/getAllLibrariesData
  // sends as `filters` -- the librariesListHandler route overwrites obj.search
  // with the top-level search term before querying Algolia, so `filters.search`
  // must carry searchText too, not just filterOptions' own (usually empty)
  // .search, or export would silently ignore the admin's free-text search.
  const handleExport = () =>
    handleExportUtil("library", toast, {
      search: searchText,
      filters: { ...filterOptions, search: searchText },
    });

  // #264008: File Name filter (upload sheet name). Stored on filterOptions so
  // the list request, Export and Bulk Edit's "all filtered" all carry it.
  const handleFileNameApply = (spec) => {
    setFilterOptions((prev) => withFileNameFilter(prev, spec));
    setApply(true);
  };

  // Fetch films from the API
  const fetchLibraries = async (page, term, filterOptions, append = false) => {
    setLoading(true);
    if (!append) {
      setFilteredResultCount(0);
    }
    try {
      const result = await getAllLibrariesData(
        limit,
        page,
        term,
        filterOptions,
      );
      if (result?.data) {
        const newRecords = result?.data?.data || [];
        if (append) {
          setRecords((prev) => [...prev, ...newRecords]);
        } else {
          setRecords(newRecords);
        }

        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);
        setFilteredResultCount(result?.data?.total);
        setHasMore(page < Math.ceil(result?.data?.total / limit));

        //filter
        if (result?.data?.facetsHeaders && result?.data?.facets) {
          const formattedFacets = updateFacetsData(result);
          setFacets(formattedFacets);

          if (
            formattedFacets &&
            formattedFacets.length > 0 &&
            currentPage === 1 &&
            !append
          ) {
            setFilterOptions((prevFilterOptions) => ({
              ...prevFilterOptions,
              filters: {
                ...formattedFacets.reduce((acc, facet) => {
                  acc[facet.key] = [];
                  return acc;
                }, {}),
                ...prevFilterOptions.filters,
              },
            }));
          }
        }
        const sortedFacets = result?.data?.sortByHeader?.map((obj) => {
          const key = Object.keys(obj)[0];
          return { key: key, display: obj[key] };
        });
        setSort(sortedFacets);
        if (
          result?.data?.page_number &&
          parseInt(result?.data?.page_number) !== page
        ) {
          setCurrentPage(parseInt(result?.data?.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching films:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreLibraries = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchLibraries(nextPage, searchText, filterOptions, true);
    }
  };

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false;
      setCurrentPage(1);
      setSearchText(searchTerm);
      const updatedOptions = { ...filterOptions, search: searchTerm };
      setFilterOptions(updatedOptions);
      setFilterPills(updatedOptions);
      fetchLibraries(1, searchTerm, updatedOptions);
    }
  };

  const getRecordHref = (record) =>
    `/libraries/${record?.alias || record?._id || record?.id}`;

  const handleEdit = (record) => {
    navigate(getRecordHref(record));
  };

  const createNewRecord = () => {
    navigate("/libraries/create");
  };
  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
    setSortOrder(newSortOrder);

    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title),
    );

    setRecords(sorted);
  };
  const handleToggleVisible = useVisibilityToggle(
    setRecords,
    updateLibraryVisibility,
    "libraries",
  );

  // Cross-tab sync: show a refresh affordance when another tab changes a record
  // that affects this grid.
  const recordsRef = useRef(records);
  recordsRef.current = records;
  const { isStale, markRefreshed } = useRecordSync("libraries", (msg) =>
    isGridChangeRelevant(msg, recordsRef.current),
  );
  const handleRefresh = () => {
    setCurrentPage(1);
    fetchLibraries(1, searchText, filterOptions);
    markRefreshed();
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    // if (isInitialLoad.current) {
    //   isInitialLoad.current = false; // Skip the first render
    //   return;
    // }

    fetchLibraries(currentPage, searchText, filterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);
  useEffect(() => {
    localStorage.setItem("viewPreference", viewMode);
  }, [viewMode]);

  useAutoOpenBulkEditPanel(
    selection.count,
    activeSidePanel,
    setActiveSidePanel,
  );

  useEffect(() => {
    if (apply) {
      window.scrollTo(0, 0);
      setApply(false);
      // Applying a filter always fetches page 1 of the new results — keep
      // currentPage in sync so the pagination UI doesn't keep showing a
      // stale later page (and so that page number isn't what gets
      // persisted/restored on a later remount).
      setCurrentPage(1);
      fetchLibraries(1, searchText, filterOptions);
      setFilterPills(filterOptions);
      // The visible record context just changed — a held selection (either
      // explicit ids or an allFiltered scope) may no longer mean what the
      // admin thinks it means.
      selection.clear();
    }
    const filtercnt = totalFilterCount(filterOptions?.filters);
    setFilterCount(filtercnt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);

  const handleClear = () => {
    handleReset();
    fetchLibraries(1, "");
    setSearchText("");
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  // Filters implementation can be added here
  const [filterPills, setFilterPills] = useState(
    persistedFilterState?.filterPills || null,
  );

  usePersistFilterState(
    LIBRARIES_SAVED_FILTERS_CONTEXT,
    { filterOptions, searchText, filterPills, currentPage },
    [filterOptions, searchText, filterPills, currentPage],
  );
  // const handleSearch = (e) => {
  //   setSearchTerm(e.target.value);
  //   handleCheckboxCh ange("searches",e.target.value)
  // };

  const handleCheckboxChange = (value, type) => {
    let selectedArray = [];
    let selectedString = "";
    switch (value) {
      case "sort":
        selectedString = filterOptions.sortBy_v;
        break;
      case "gate":
        selectedString = filterOptions.gate;
        break;
      case "searches":
        selectedString = filterOptions.search;
        break;
      default:
        if (value in filterOptions.filters) {
          selectedArray = filterOptions.filters[value];
        }
        break;
    }

    if (Array.isArray(type)) {
      // Bulk replace (used by FilterBody's "Select All" / clear-all toggle)
      // rather than the usual single-value toggle below.
      selectedArray = type;
    } else if (selectedArray.includes(type)) {
      selectedArray = selectedArray.filter((checkbox) => checkbox !== type);
    } else {
      selectedArray = [...selectedArray, type];
    }

    if (selectedString !== type) {
      selectedString = type;
    }

    switch (value) {
      case "sort":
        setFilterOptions((prevFilterOptions) => ({
          ...prevFilterOptions,
          sortBy_v: selectedString,
        }));
        break;
      case "gate":
        setFilterOptions((prevFilterOptions) => ({
          ...prevFilterOptions,
          gate: selectedString,
        }));
        break;
      case "searches":
        setFilterOptions((prevFilterOptions) => ({
          ...prevFilterOptions,
          search: selectedString,
        }));
        break;
      default:
        if (value in filterOptions.filters) {
          setFilterOptions((prevFilterOptions) => ({
            ...prevFilterOptions,
            filters: {
              ...prevFilterOptions.filters,
              [value]: selectedArray,
            },
          }));
        }
        break;
    }
  };
  const handleReset = () => {
    setFilteredResultCount(0);
    const initializedFilters = facets.reduce((acc, facet) => {
      acc[facet.key] = [];
      return acc;
    }, {});
    setFilterOptions({
      search: "",
      filters: initializedFilters,
      sortBy_v: "sortable_year",
      gate: "AND",
    });
    setFilterPills({
      search: "",
      filters: initializedFilters,
      sortBy_v: "sortable_year",
      gate: "AND",
    });
    setFilterCount(0);
    setSearchText("");
    // Without this, clearing filters while on page 3+ left currentPage
    // stuck there — previously masked because every remount happened to
    // default it back to 1 anyway; now that page state persists across a
    // navigate-away-and-back round trip, a stale page number would too.
    setCurrentPage(1);
  };

  // Restores a saved filter's snapshot into this page's own filter state,
  // then re-triggers the existing Apply flow — no new fetch/query logic.
  const applyFilterState = (savedFilterConfig) => {
    setFilterOptions(savedFilterConfig);
    setSearchText(savedFilterConfig?.search || "");
    setApply(true);
  };

  const columns = [
    {
      accessor: "title",
      label: "Title",
    },
    {
      accessor: "file_name",
      label: "File Name",
    },
    {
      accessor: "isVisible",
      label: "Is Visible",
    },
    {
      accessor: "direct_url_of_preview_image",
      label: "Image",
    },
    {
      accessor: "sortable_year",
      label: "Sortable Year",
    },
  ];

  return (
    <>
      <Stack
        spacing={0}
        width="100%"
        height="calc(100vh - 120px)"
        mx="auto"
        bg="white"
        p="20px"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <DataToolbar
          title="Library"
          onRefresh={isStale ? handleRefresh : undefined}
          totalRecords={totalRecords}
          addOption
          createNewRecord={createNewRecord}
          handleSearch={handleSearch}
          handleClear={handleClear}
          type="libraries"
          sort={sort}
          filterOptions={filterOptions}
          handleCheckboxChange={handleCheckboxChange}
          setApply={setApply}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onFilterToggle={() =>
            setActiveSidePanel((prev) => (prev === "filter" ? null : "filter"))
          }
          showFilter={activeSidePanel === "filter"}
          sortRecords={sortRecords}
          sortOrder={sortOrder}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          showSearch={true}
          pagination={false}
          exportHandler={SHOW_EXPORT ? handleExport : undefined}
        />

        <FileNameFilter
          value={filterOptions?.fileName}
          onApply={handleFileNameApply}
        />

        {((filterPills && filterPills?.search) || filterCount > 0) && (
          <Box
            px={4}
            py={2}
            bg="white"
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            <FilterPills
              filters={filterPills}
              setFilterOptions={setFilterOptions}
              setApply={setApply}
              setSearchTerm={setSearchText}
              setFilterPills={setFilterPills}
              filteredResultCount={filteredResultCount}
            />
          </Box>
        )}

        <SelectionToolbar
          viewMode={viewMode}
          records={records}
          totalRecords={totalRecords}
          selection={selection}
        />

        <HStack
          flex="1"
          width={"100%"}
          alignItems={"flex-start"}
          spacing={0}
          overflow="hidden"
        >
          <Box
            flex="1"
            height="100%"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            px={4}
          >
            {viewMode === "list" ? (
              <ListView
                loading={loading}
                records={records}
                columns={columns}
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
                handleEdit={handleEdit}
                selectable
                selectedRowKeys={selection.selectedIds}
                onRowSelect={(key, checked) =>
                  selection.toggleOne(key, checked)
                }
                onSelectAll={(checked) =>
                  selection.selectMany(
                    records.map((r) => r?._id).filter(Boolean),
                    checked,
                  )
                }
                pagination={true}
                getHref={getRecordHref}
              />
            ) : (
              <GridView
                data={records}
                fetchMoreData={fetchMoreLibraries}
                hasMore={hasMore}
                handleEdit={handleEdit}
                loading={loading}
                onToggleVisible={handleToggleVisible}
                getHref={getRecordHref}
                selectable
                isRecordSelected={(record) => selection.isSelected(record?._id)}
                onToggleSelect={(record, checked) =>
                  selection.toggleOne(record?._id, checked)
                }
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            )}
          </Box>

          {activeSidePanel === "filter" && (
            <Stack
              marginRight={"0px!important"}
              right={"0px"}
              top={"20px"}
              pos={"sticky"}
              pt="4"
            >
              <Box px={1} pb={2}>
                <SavedFiltersPanel
                  context={LIBRARIES_SAVED_FILTERS_CONTEXT}
                  currentFilterState={filterOptions}
                  applyFilterState={applyFilterState}
                />
              </Box>
              {facets && facets?.length > 0 && (
                <FilterBox
                  setApply={setApply}
                  setShowFilterBox={() => setActiveSidePanel(null)}
                  explore={explore}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSearch={handleSearch}
                  filterOptions={filterOptions}
                  filteroptionData={facets}
                  setFilterOptions={setFilterOptions}
                  setSearchTerm={setSearchText}
                  filterCount={filterCount}
                  searchTerm={searchText}
                  handleReset={handleReset}
                  className={"box-filter-film"}
                  showSearch={true}
                />
              )}
            </Stack>
          )}

          {activeSidePanel === "bulkEdit" && (
            <Box
              marginRight={"0px!important"}
              right={"0px"}
              top={"20px"}
              pos={"sticky"}
              pt="4"
            >
              <BulkEditPanel
                selection={selection}
                filterOptions={filterOptions}
                records={records}
                onClose={() => setActiveSidePanel(null)}
                onApplied={() => {
                  // Keep the selection and the panel open on success — the
                  // admin may want to apply another change to the same
                  // selection right away. Just refresh the underlying data.
                  fetchLibraries(currentPage, searchText, filterOptions);
                }}
              />
            </Box>
          )}
        </HStack>
      </Stack>
    </>
  );
};

export default LibrariesTable;
